/**
 * Analytics tracking for Supernal TTS Widget
 */

import { AnalyticsEvent, AnalyticsSession } from '../types';

export class AnalyticsManager {
  private enableAnalytics: boolean;
  private analyticsSession?: AnalyticsSession;
  private eventQueue: AnalyticsEvent[] = [];
  private analyticsFlushInterval: number | null = null;
  private apiUrl: string;
  private version: string;
  private devMode: boolean;

  constructor(enableAnalytics: boolean, apiUrl: string, version: string, devMode: boolean, referralCode?: string) {
    this.enableAnalytics = enableAnalytics;
    this.apiUrl = apiUrl;
    this.version = version;
    this.devMode = devMode;

    if (this.enableAnalytics) {
      this.initializeAnalytics(referralCode);
    }
  }

  /**
   * Initialize analytics session with origin tracking and referral detection
   */
  private initializeAnalytics(referralCodeOption?: string): void {
    const sessionId = this.generateSessionId();
    const url = new URL(window.location.href);
    const utmParams = this.getUTMParams(url);
    const referralCode = this.getReferralCode(url, referralCodeOption);

    this.analyticsSession = {
      sessionId,
      originDomain: window.location.hostname,
      originUrl: window.location.href,
      referrerUrl: document.referrer || undefined,
      referralCode,
      utmParams,
      startTime: Date.now(),
      events: []
    };

    // Set up automatic flushing every 10 seconds
    this.setupAnalyticsFlush();

    // Flush on page unload
    window.addEventListener('beforeunload', () => {
      this.flushAnalytics();
    });

    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.flushAnalytics();
      }
    });
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `sess_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Extract UTM parameters from URL
   */
  private getUTMParams(url: URL): AnalyticsSession['utmParams'] {
    return {
      source: url.searchParams.get('utm_source') || undefined,
      medium: url.searchParams.get('utm_medium') || undefined,
      campaign: url.searchParams.get('utm_campaign') || undefined,
      term: url.searchParams.get('utm_term') || undefined,
      content: url.searchParams.get('utm_content') || undefined,
    };
  }

  /**
   * Get referral code from URL or options
   * Checks URL params first, then falls back to provided option
   */
  private getReferralCode(url: URL, referralCodeOption?: string): string | undefined {
    // Check URL parameters first (allows override)
    const urlReferral = url.searchParams.get('ref') ||
                        url.searchParams.get('referral') ||
                        url.searchParams.get('referral_code');

    return urlReferral || referralCodeOption;
  }

  /**
   * Track an analytics event
   */
  public trackEvent(
    eventType: AnalyticsEvent['eventType'],
    data: Partial<AnalyticsEvent> = {}
  ): void {
    if (!this.enableAnalytics || !this.analyticsSession) {
      return;
    }

    const event: AnalyticsEvent = {
      eventType,
      sessionId: this.analyticsSession.sessionId,
      originDomain: this.analyticsSession.originDomain,
      originUrl: this.analyticsSession.originUrl,
      referrerUrl: this.analyticsSession.referrerUrl,
      referralCode: this.analyticsSession.referralCode,
      utmSource: this.analyticsSession.utmParams.source,
      utmMedium: this.analyticsSession.utmParams.medium,
      utmCampaign: this.analyticsSession.utmParams.campaign,
      utmTerm: this.analyticsSession.utmParams.term,
      utmContent: this.analyticsSession.utmParams.content,
      widgetVersion: this.version,
      timestamp: Date.now(),
      ...data
    };

    this.eventQueue.push(event);
    this.analyticsSession.events.push(event);

    // Flush if queue is large
    if (this.eventQueue.length >= 10) {
      this.flushAnalytics();
    }
  }

  /**
   * Set up automatic analytics flushing
   */
  private setupAnalyticsFlush(): void {
    // Flush every 10 seconds
    this.analyticsFlushInterval = window.setInterval(() => {
      this.flushAnalytics();
    }, 10000);
  }

  /**
   * Send analytics events to the API
   */
  public async flushAnalytics(): Promise<void> {
    if (!this.enableAnalytics || this.eventQueue.length === 0) {
      return;
    }

    const events = [...this.eventQueue];
    this.eventQueue = [];

    try {
      // Send to analytics endpoint (silent failure - don't block widget functionality)
      await fetch(`${this.apiUrl}/api/v1/analytics/widget-events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ events }),
        // Use keepalive for beforeunload events
        keepalive: true
      }).catch(() => {
        // Silently fail - analytics should never break the widget
      });
    } catch (error) {
      // Silently fail - analytics should never break the widget
      if (this.devMode) {
        console.warn('[TTS Widget] Analytics flush failed:', error);
      }
    }
  }

  /**
   * Track branding badge click (click-through to main site)
   */
  public trackBrandingClick(clickThroughUrl: string): void {
    this.trackEvent('click_through', {
      clickThroughUrl
    });
    // Flush immediately for click-through events
    this.flushAnalytics();
  }

  /**
   * Clean up analytics resources
   */
  public destroy(): void {
    if (this.analyticsFlushInterval) {
      clearInterval(this.analyticsFlushInterval);
      this.analyticsFlushInterval = null;
    }
    this.flushAnalytics();
  }
}
